import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../axios-Instance";
import { getLoginToken } from "../storage";
import { queryKeys } from "../react-query/constants";
import { successAlert, toastOptions } from "../utils";
import { toast } from "react-toastify";

const SERVER_ERROR = "There was an error contacting the server.";

async function createAccountability(formData: any) {
  const data = await axiosInstance({
    url: "/accountability",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

async function approveAccountability(formData: any) {
  const data = await axiosInstance({
    url: `/accountability/journeys/${formData?._id}/approve`,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

async function makePayment(formData: any) {
  const data = await axiosInstance({
    url: `/accountability/journeys/${formData?._id}/paid`,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

export function useCreateAccountability() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => createAccountability(formData),
    onSuccess: () => {
      queryClient.invalidateQueries([
        queryKeys.accountability,
        queryKeys.myJourney,
      ]);
      successAlert("Accountability created successfully");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useApproveAccountability() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset, isLoading } = useMutation({
    mutationFn: (formData) => approveAccountability(formData),
    onSuccess: () => {
      queryClient.invalidateQueries([
        queryKeys.accountability,
        queryKeys.myJourney,
      ]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset, isLoading };
}
export function usePayAccountability() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset, isLoading } = useMutation({
    mutationFn: (formData) => makePayment(formData),
    onSuccess: () => {
      queryClient.invalidateQueries([
        queryKeys.accountability,
        queryKeys.myJourney,
      ]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset, isLoading };
}

async function allAccountability() {
  const data = await axiosInstance({
    url: "/accountability",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

async function getMyJourney() {
  const data = await axiosInstance({
    url: "/accountability/me",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

export function useGetAccountability() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.accountability],
    queryFn: allAccountability,
  });
  return data;
}

export function useMyAccountability() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.myJourney],
    queryFn: getMyJourney,
  });
  return data;
}

async function updateAccountability(payload: {
  id: string;
  questions: Array<{
    tempId: string;
    title: string;
    body: string;
    documents: File[];
  }>;
  questionDocumentMap: Array<{
    tempId: string;
    fileName: string;
    size: number;
    lastModified: number;
  }>;
  journeyDocuments: File[];
}) {
  const formData = new FormData();

  formData.append("_id", payload.id);

  formData.append(
    "questions",
    JSON.stringify(
      payload.questions.map((item) => ({
        tempId: item.tempId,
        title: item.title,
        body: item.body,
      })),
    ),
  );

  formData.append(
    "questionDocumentMap",
    JSON.stringify(payload.questionDocumentMap),
  );

  payload.journeyDocuments.forEach((file) => {
    formData.append("journeyDocuments", file);
  });

  payload.questions.forEach((question) => {
    question.documents.forEach((file) => {
      formData.append("questionDocuments", file);
    });
  });

  const data = await axiosInstance({
    url: `/journey/admin/${payload.id}`,
    method: "PUT",
    data: formData,
    headers: {
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

export function useUpdateAccountability() {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isSuccess, reset, isLoading } = useMutation({
    mutationFn: (payload: {
      id: string;
      questions: Array<{
        tempId: string;
        title: string;
        body: string;
        documents: File[];
      }>;
      questionDocumentMap: Array<{
        tempId: string;
        fileName: string;
        size: number;
        lastModified: number;
      }>;
      journeyDocuments: File[];
    }) => updateAccountability(payload),

    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.user, queryKeys.myJourney]);
    },

    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;

      toast.error(err, toastOptions);
    },
  });

  return {
    mutate,
    isError,
    error,
    isSuccess,
    reset,
    isLoading,
  };
}

async function deleteAccountability(accountabilityId: string) {
  const data = await axiosInstance({
    url: `/accountability/${accountabilityId}`,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

export function useDeleteAccountability() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => deleteAccountability(formData as any),
    onSuccess: () => {
      queryClient.invalidateQueries([
        queryKeys.accountability,
        queryKeys.myJourney,
      ]);
      successAlert("Accountability deleted successfully");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

async function respondToAccountabilityFramework(payload: {
  journeyId: string;
  frameworkId: string;
  body: string;
  responseDocuments: File[];
}) {
  const formData = new FormData();

  formData.append("body", payload.body || "");

  payload.responseDocuments.forEach((file) => {
    formData.append("responseDocuments", file);
  });

  const data = await axiosInstance({
    url: `/accountability/${payload.journeyId}/frameworks/${payload.frameworkId}/respond`,
    method: "POST",
    data: formData,
    headers: {
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

async function closeAccountabilityFramework(payload: { id: string }) {
  const data = await axiosInstance({
    url: `/accountability/${payload.id}/frameworks/close`,
    method: "POST",
    data: {},
    headers: {
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

export function useRespondToAccountabilityFramework() {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isSuccess, reset, isLoading } = useMutation({
    mutationFn: (payload: {
      journeyId: string;
      frameworkId: string;
      body: string;
      responseDocuments: File[];
    }) => respondToAccountabilityFramework(payload),

    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.user, queryKeys.myJourney]);
      successAlert("Response submitted successfully");
    },

    onError: (error: any) => {
      const err = error?.response?.data?.error || SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });

  return {
    mutate,
    isError,
    error,
    isSuccess,
    reset,
    isLoading,
  };
}

export function useCloseAccountabilityFramework() {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isSuccess, reset, isLoading } = useMutation({
    mutationFn: (payload: { id: string }) =>
      closeAccountabilityFramework(payload),

    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.user, queryKeys.myJourney]);
      successAlert("Successfully closed accountability framework");
    },

    onError: (error: any) => {
      const err = error?.response?.data?.error || SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });

  return {
    mutate,
    isError,
    error,
    isSuccess,
    reset,
    isLoading,
  };
}
